import { ReactNode } from 'react'

import { FieldSkeleton } from '@/components/layout/field-row'
import { DetailsList, DetailsListItem } from '@/components/ui/details-list'
import { Flex } from '@/components/ui/flex'
import { Typography } from '@/components/ui/typography'

import * as S from './details-skeleton.styled'

const AVATAR_SIZE = '7.2rem'
// Note: text-large line-height of the person name
const NAME_HEIGHT = '2.4rem'
// Note: small Badge = 1.6rem line-height + 2 * 0.4rem padding, taller than a text row's `1lh`
const BADGE_HEIGHT = '2.4rem'
const VALUE_WIDTH = '14rem'
// Note: any number that cannot show up inside a title, only used to find where the count goes
const COUNT_MARKER = 987654

interface DetailsRowsProps {
  // Note: index of the row that holds a Badge (a loaded row with a badge is taller than a text one)
  badgeRow?: number
}

// Note: `1lh` is the row text's own line-height, so a row is as tall as a loaded one
const getRowHeight = (index: number, badgeRow?: number) =>
  index === badgeRow ? BADGE_HEIGHT : '1lh'

// Note: labels are static text, so a labelled row keeps its real label and only the value is a skeleton
export const DetailsLabelledRowsSkeleton = ({
  badgeRow,
  labels,
}: DetailsRowsProps & { labels: string[] }) => (
  <DetailsList>
    {labels.map((label, index) => (
      <DetailsListItem key={label} label={label}>
        <FieldSkeleton height={getRowHeight(index, badgeRow)} width={VALUE_WIDTH} />
      </DetailsListItem>
    ))}
  </DetailsList>
)

// Note: for rows that are data themselves (a trainer's services, a manager's buildings) - there is
// no static label, so the whole row is one wide skeleton
export const DetailsRowsSkeleton = ({ badgeRow, rows }: DetailsRowsProps & { rows: number }) => (
  <DetailsList>
    {Array.from({ length: rows }, (_, index) => (
      <S.SkeletonRow key={index}>
        <FieldSkeleton height={getRowHeight(index, badgeRow)} width="100%" />
      </S.SkeletonRow>
    ))}
  </DetailsList>
)

interface CountedTitleSkeletonProps {
  // Note: the same translation call as the loaded title, called with a marker count
  format: (count: number) => string
}

// Note: "Services (2)" while the count is unknown: the word stays real, the whole "(2)" is a skeleton
const COUNT_PATTERN = new RegExp(String.raw`\s*[(\[]?${COUNT_MARKER}[)\]]?`)

export const CountedTitleSkeleton = ({ format }: CountedTitleSkeletonProps) => {
  const title = format(COUNT_MARKER)
  const match = COUNT_PATTERN.exec(title)
  if (!match) return <>{title}</>

  return (
    <>
      {title.slice(0, match.index)}
      <S.CountSkeleton height="1.2rem" width="2rem" />
      {title.slice(match.index + match[0].length)}
    </>
  )
}

export interface DetailsSkeletonSection extends DetailsRowsProps {
  // Note: real labels of a section of known rows; without them `rows` whole-row skeletons are shown
  labels?: string[]
  rows?: number
  title: ReactNode
}

const DetailsSectionSkeleton = ({ badgeRow, labels, rows = 1, title }: DetailsSkeletonSection) => (
  <Flex direction="column" gap={8}>
    <Typography color="black800" size="text-small" weight="semibold">
      {title}
    </Typography>
    {labels ? (
      <DetailsLabelledRowsSkeleton badgeRow={badgeRow} labels={labels} />
    ) : (
      <DetailsRowsSkeleton badgeRow={badgeRow} rows={rows} />
    )}
  </Flex>
)

interface DetailsModalSkeletonProps {
  sections: DetailsSkeletonSection[]
}

// Note: titles and labels are static text, so only the person (avatar, name) and the values are skeletons
export const DetailsModalSkeleton = ({ sections }: DetailsModalSkeletonProps) => (
  <Flex aria-hidden direction="column" gap={20}>
    <Flex align="center" gap={16}>
      <FieldSkeleton height={AVATAR_SIZE} width={AVATAR_SIZE} />
      <FieldSkeleton height={NAME_HEIGHT} width="20rem" />
    </Flex>

    {sections.map((section, index) => (
      <DetailsSectionSkeleton key={index} {...section} />
    ))}
  </Flex>
)
